import { useQuery } from '@tanstack/react-query';
import { Train } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { api, type PublicBoard as PublicBoardData, type PublicBoardFlag } from '@/lib/api';
import AllianceLogo from '@/components/AllianceLogo';
import VsWeekChip from '@/components/VsWeekChip';

function formatExpiry(iso: string | null): string {
  if (!iso) return 'no expiry';
  const t = new Date(iso).getTime();
  const days = Math.round((t - Date.now()) / (24 * 60 * 60 * 1000));
  if (days < 0) return 'expired';
  if (days === 0) return 'expires today';
  if (days === 1) return 'expires tomorrow';
  return `expires in ${days} days`;
}

const FLAG_META: Record<
  'red' | 'yellow' | 'green',
  { title: string; badge: string; dot: string; bg: string }
> = {
  red: {
    title: 'Red cards',
    badge: 'text-red-200',
    dot: 'bg-red-500',
    bg: 'bg-red-900/20 border-red-900/60',
  },
  yellow: {
    title: 'Yellow cards',
    badge: 'text-yellow-200',
    dot: 'bg-yellow-400',
    bg: 'bg-yellow-900/20 border-yellow-900/60',
  },
  green: {
    title: 'Should be picked',
    badge: 'text-emerald-200',
    dot: 'bg-emerald-500',
    bg: 'bg-emerald-900/20 border-emerald-900/60',
  },
};

export default function PublicBoard() {
  const { slug } = useParams();
  const query = useQuery({
    queryKey: ['public-board', slug ?? null],
    queryFn: () => api.getPublicBoard(slug),
    retry: false,
  });
  const alliance = query.data?.alliance;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/60">
        <div className="mx-auto flex max-w-5xl items-center gap-4 px-4 py-4">
          <AllianceLogo alliance={alliance} className="h-14" />
          <div>
            <div className="text-lg font-semibold">
              {alliance ? `${alliance.tag || alliance.name} — Alliance Board` : 'Alliance Board'}
            </div>
            {(alliance?.motto || alliance?.tag) && (
              <div className="text-xs text-slate-400">{alliance.motto || alliance.name}</div>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-6 px-4 py-6">
        {query.isLoading && <div className="text-slate-400">Loading…</div>}
        {query.isError && (
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-sm text-red-300">
            Board unavailable.
          </div>
        )}

        <VsWeekChip variant="public" />

        {query.data && (
          <>
            <TrainsCard board={query.data} />
            <CardLegend />
            <FlagsSection board={query.data} />
            <TrackingSection board={query.data} />
          </>
        )}
      </main>
    </div>
  );
}

function TrainsCard({ board }: { board: PublicBoardData }) {
  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <h2 className="mb-3 flex items-center gap-2 text-base font-semibold">
        <Train size={18} /> Train conductors this week
      </h2>
      {board.trainWeekNote && (
        <div className="mb-3 whitespace-pre-wrap rounded border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-200">
          {board.trainWeekNote}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-7">
        {board.trains.map((t) => (
          <div
            key={t.date}
            className="rounded border border-slate-800 bg-slate-950/60 p-2"
          >
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              {t.weekday} · {t.date.slice(5)}
            </div>
            <div className="mt-0.5 truncate text-sm">
              {t.name ?? <span className="text-slate-600">—</span>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CardLegend() {
  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-300">
      <h2 className="mb-2 text-base font-semibold text-slate-100">
        How the card system works
      </h2>
      <ul className="space-y-1.5 list-disc pl-5 text-sm">
        <li>
          <span className="font-semibold text-yellow-200">Yellow cards</span>{' '}
          barely affect selection — they're a gentle nudge, not a penalty. But{' '}
          <span className="font-semibold">two active yellow cards count as a red card.</span>
        </li>
        <li>
          <span className="font-semibold text-red-300">Red cards</span> mean the
          player sits out CS and DS for the upcoming week and is not eligible as
          a train conductor.
        </li>
        <li>
          <span className="font-semibold text-emerald-300">Should be picked</span>{' '}
          is a green flag with a 7-day validity that strongly prioritises the
          player in the next event autopick.
        </li>
      </ul>
    </section>
  );
}

function FlagsSection({ board }: { board: { flags: { red: PublicBoardFlag[]; yellow: PublicBoardFlag[]; green: PublicBoardFlag[] } } }) {
  const kinds: Array<'red' | 'yellow' | 'green'> = ['red', 'yellow', 'green'];
  return (
    <section className="grid gap-4 md:grid-cols-3">
      {kinds.map((k) => (
        <FlagCard key={k} kind={k} flags={board.flags[k]} />
      ))}
    </section>
  );
}

function FlagCard({
  kind,
  flags,
}: {
  kind: 'red' | 'yellow' | 'green';
  flags: PublicBoardFlag[];
}) {
  const meta = FLAG_META[kind];
  return (
    <div className={`rounded-lg border p-4 ${meta.bg}`}>
      <h3 className={`mb-2 flex items-center gap-2 text-sm font-semibold ${meta.badge}`}>
        <span className={`inline-block h-2.5 w-2.5 rounded-full ${meta.dot}`} />
        {meta.title}
        <span className="ml-auto text-xs text-slate-500">{flags.length}</span>
      </h3>
      {flags.length === 0 ? (
        <div className="text-xs text-slate-500">None.</div>
      ) : (
        <ul className="space-y-2">
          {flags.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="rounded border border-slate-800/70 bg-slate-950/40 px-2 py-1.5"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium">{f.name}</div>
                <div className="text-[10px] text-slate-500">
                  {formatExpiry(f.expiresAt)}
                </div>
              </div>
              {f.note && (
                <div className="mt-1 whitespace-pre-wrap text-xs text-slate-300">
                  {f.note}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TrackingSection({
  board,
}: {
  board: { shouldBePicked: string[]; recentNoShows: string[] };
}) {
  return (
    <section className="grid gap-4 md:grid-cols-2">
      <NameList
        title="Should be picked next"
        names={board.shouldBePicked}
        accent="text-orange-200"
        dot="bg-orange-400"
      />
      <NameList
        title="Recent no-shows"
        names={board.recentNoShows}
        accent="text-red-200"
        dot="bg-red-500"
      />
    </section>
  );
}

function NameList({
  title,
  names,
  accent,
  dot,
}: {
  title: string;
  names: string[];
  accent: string;
  dot: string;
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <h3 className={`mb-2 flex items-center gap-2 text-sm font-semibold ${accent}`}>
        <span className={`inline-block h-2.5 w-2.5 rounded-full ${dot}`} />
        {title}
        <span className="ml-auto text-xs text-slate-500">{names.length}</span>
      </h3>
      {names.length === 0 ? (
        <div className="text-xs text-slate-500">None.</div>
      ) : (
        <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          {names.map((n) => (
            <li
              key={n}
              className="truncate rounded border border-slate-800/70 bg-slate-950/40 px-2 py-1 text-sm"
            >
              {n}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
